import {
  SIDEBAR_BREAKPOINT,
  useSidebar,
} from "@/lib/components/sidebar/sidebar.provider";
import AdaptiveMenu, {
  AdaptiveMenuContent,
  AdaptiveMenuTrigger,
} from "@/lib/components/ui/adaptive-menu/adaptive-menu";
import EllipsisVertical from "@/lib/components/ui/icons/ellipsis-vertical";
import X from "@/lib/components/ui/icons/x";
import Spinner from "@/lib/components/ui/spinner";
import Text from "@/lib/components/ui/text/text";
import useMediaQuery from "@/lib/hooks/use-media-query";
import { log } from "@soulbyte/core";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import {
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type HTMLAttributes,
} from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  pillRowVariants as listItemVariants,
  pillActionVariants as listItemTriggerVariants,
} from "@/lib/components/ui/list-pill/list-pill";
import { useRenameThread } from "../../hooks/use-rename-thread";
import { useThreadsContext } from "../../threads.context";
import { isReadOnlyChannel, threadChannelIcon } from "../../thread-source";
import type { Thread } from "../../threads.utils";
import { ThreadsListDelete } from "./threads-list.delete";
import { ThreadsListPin } from "./threads-list.pin";
import { ThreadsListRename } from "./threads-list.rename";

export default function ThreadsList({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLUListElement>) {
  return (
    <ul className={cn("flex flex-col gap-1", className)} {...props}>
      {children}
    </ul>
  );
}

export function ThreadsListHeader({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLElement>) {
  // matches the module list's GroupHeader: same left padding and bottom margin
  return (
    <header
      className={cn("flex items-center gap-2 px-3 pb-1", className)}
      {...props}
    >
      {children}
    </header>
  );
}

export function ThreadsListTitle({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  // same treatment as the module list section labels (Im Dock, Verfügbar)
  return (
    <Text
      size="sm"
      variant="label"
      className={cn("text-foreground-pressed min-w-0 truncate", className)}
      {...props}
    >
      {children}
    </Text>
  );
}

export function ThreadsListItem(
  props: HTMLAttributes<HTMLLIElement> & { thread: Thread },
) {
  const matches = useMediaQuery(SIDEBAR_BREAKPOINT);

  return matches ? (
    <ThreadsListItemWithDoubleClick {...props} />
  ) : (
    <ThreadsListItemWithLongPress {...props} />
  );
}

function ThreadsListItemWithLongPress({
  thread,
  className,
  ...props
}: HTMLAttributes<HTMLLIElement> & { thread: Thread }) {
  const navigate = useNavigate();
  const { assistant } = useThreadsContext();

  const { setOpen } = useSidebar();
  const [menuOpen, setMenuOpen] = useState(false);

  const [searchParams] = useSearchParams();
  const isActive = searchParams.get("threadId") === thread.id;

  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);

  function handleClick() {
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return;
    }

    if (isActive) return;
    if (!assistant.id || !thread.id) {
      log.error(
        "Cannot navigate to thread: Assistant or thread ID is missing",
        { extra: { thread, assistant } },
      );
      return;
    }

    setOpen(false);
    navigate(`/chat?assistantId=${assistant.id}&threadId=${thread.id}`);
  }

  function handleLongPress() {
    setMenuOpen(true);
    longPressTriggeredRef.current = true;
  }

  function handlePressStart(e: React.TouchEvent | React.MouseEvent) {
    e.preventDefault();
    longPressTriggeredRef.current = false;
    longPressTimerRef.current = setTimeout(handleLongPress, 500);
  }

  function handlePressEnd(e: React.TouchEvent | React.MouseEvent) {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    if (longPressTriggeredRef.current) {
      e.preventDefault();
    }
  }

  // Clean up the long press timer when the component unmounts
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  if (!assistant.id) return null;

  return (
    <li
      className={cn(listItemVariants({ active: isActive }), className)}
      {...props}
    >
      <ThreadsListItemSourceIcon source={thread.source} />
      <ThreadsListItemTitle
        title={thread.title}
        className="touch-none select-none"
        onClick={handleClick}
        onTouchStart={handlePressStart}
        onTouchEnd={handlePressEnd}
        onMouseDown={handlePressStart}
        onMouseUp={handlePressEnd}
        onMouseLeave={handlePressEnd}
        onContextMenu={(e) => e.preventDefault()}
      />
      <ThreadsListAdaptiveMenu
        open={menuOpen}
        thread={thread}
        isActive={isActive}
        assistantId={assistant.id}
        onOpenChange={setMenuOpen}
      />
    </li>
  );
}

function ThreadsListItemWithDoubleClick({
  thread,
  className,
  ...props
}: HTMLAttributes<HTMLLIElement> & { thread: Thread }) {
  const navigate = useNavigate();
  const { assistant } = useThreadsContext();

  const [searchParams] = useSearchParams();
  const isActive = searchParams.get("threadId") === thread.id;

  const [isEditing, setIsEditing] = useState(false);
  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [title, setTitle] = useState(thread.title || "");
  const previousTitle = thread.title || "";

  const { rename, isPending } = useRenameThread({
    thread,
    onError: () => setTitle(previousTitle),
    onSuccess: () => setIsEditing(false),
    assistantId: assistant.id ?? "",
  });

  // Clean up the click timeout when the component unmounts
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
    };
  }, []);

  // Reset editing state when Escape is pressed
  useEffect(() => {
    if (!isEditing) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setTitle(previousTitle);
        setIsEditing(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isEditing, previousTitle]);

  function handleClick() {
    if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);

    clickTimeoutRef.current = setTimeout(() => {
      clickTimeoutRef.current = null;
      if (isActive) return;

      if (!assistant.id || !thread.id) {
        log.error(
          "Cannot navigate to thread: Assistant or thread ID is missing",
          { extra: { thread, assistant } },
        );
        return;
      }

      navigate(`/chat?assistantId=${assistant.id}&threadId=${thread.id}`);
    }, 250); // Have to double click within 250ms to trigger editing
  }

  function handleDoubleClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }

    if (!assistant.id) return;
    setTitle(thread.title || "");
    setIsEditing(true);
  }

  function handleSubmit() {
    const trimmed = title.trim();
    if (!trimmed || trimmed === previousTitle) {
      setTitle(previousTitle);
      setIsEditing(false);
      return;
    }
    rename(title, previousTitle);
  }

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  }

  function handleCancel() {
    setTitle(previousTitle);
    setIsEditing(false);
  }

  if (!assistant.id) return null;

  return (
    <li
      className={cn(listItemVariants({ active: isActive }), className)}
      {...props}
    >
      {isEditing ? (
        <>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleInputKeyDown}
            autoFocus
            className="placeholder:text-foreground-pressed min-w-0 grow truncate rounded-none border-0 bg-transparent text-inherit outline-none"
          />
          <button
            type="button"
            onClick={handleCancel}
            className={cn(listItemTriggerVariants(), "shrink-0")}
            aria-label="Close"
          >
            {isPending ? <Spinner /> : <X />}
          </button>
        </>
      ) : (
        <>
          <ThreadsListItemSourceIcon source={thread.source} />
          <ThreadsListItemTitle
            title={thread.title}
            onClick={handleClick}
            onDoubleClick={
              isReadOnlyChannel(thread.source) ? undefined : handleDoubleClick
            }
          />
          <ThreadsListAdaptiveMenu
            thread={thread}
            isActive={isActive}
            assistantId={assistant.id}
          />
        </>
      )}
    </li>
  );
}

// Channel icon on the left of each row: robot for website, phone for calls,
// chat bubble for the caller's own chats.
function ThreadsListItemSourceIcon({ source }: { source?: string }) {
  const { t } = useTranslation();
  const { Icon, labelKey } = threadChannelIcon(source);
  return (
    <Icon role="img" aria-label={t(labelKey)} className="size-4 shrink-0" />
  );
}

function ThreadsListAdaptiveMenu({
  thread,
  isActive: _isActive,
  assistantId,
  ...props
}: ComponentProps<typeof AdaptiveMenu> & {
  thread: Thread;
  isActive: boolean;
  assistantId: string;
}) {
  return (
    <AdaptiveMenu {...props}>
      <AdaptiveMenuTrigger className={cn(listItemTriggerVariants())}>
        <EllipsisVertical />
      </AdaptiveMenuTrigger>
      <AdaptiveMenuContent align="end">
        <ThreadsListPin thread={thread} assistantId={assistantId} />
        <ThreadsListRename thread={thread} assistantId={assistantId} />
        <ThreadsListDelete thread={thread} assistantId={assistantId} />
      </AdaptiveMenuContent>
    </AdaptiveMenu>
  );
}

const TITLE_SCROLL_PX_PER_SECOND = 40;

// replaces the old truncation tooltip: hovering the row slides the clipped
// part of the title into view at a constant speed, then back on mouse-out
function ThreadsListItemTitle({
  title,
  className,
  ...props
}: ComponentProps<"button"> & { title?: string }) {
  const { ref, overflowPx } = useTitleOverflow(title);

  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        "not-interactive min-w-0 grow cursor-pointer truncate text-left group-hover:text-clip before:absolute before:inset-0",
        className,
      )}
      {...props}
    >
      <span
        className="inline-block transition-transform delay-300 ease-linear group-hover:-translate-x-(--title-overflow) motion-reduce:transition-none"
        style={
          {
            "--title-overflow": `${overflowPx}px`,
            transitionDuration: `${(overflowPx / TITLE_SCROLL_PX_PER_SECOND) * 1000}ms`,
          } as CSSProperties
        }
      >
        {title}
      </span>
    </button>
  );
}

function useTitleOverflow(title?: string) {
  const ref = useRef<HTMLButtonElement>(null);
  const [overflowPx, setOverflowPx] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const update = () =>
      setOverflowPx(Math.max(0, element.scrollWidth - element.clientWidth));

    update();

    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [title]);

  return { ref, overflowPx };
}
